import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const PAGE_SIZES = [25, 50, 100];

// CRM-PAGINATION-001: list state (page, page_size, search, filters) lives in the URL so Back returns to the same page/filters.
export const usePageParams = (defaults = {}) => {
  const [sp, setSp] = useSearchParams();
  const get = (k) => sp.get(k) ?? defaults[k] ?? '';
  const page = Math.max(1, parseInt(sp.get('page') || '1', 10) || 1);
  const pageSize = PAGE_SIZES.includes(Number(sp.get('page_size'))) ? Number(sp.get('page_size')) : 25;
  const update = (patch, { resetPage = true } = {}) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => { if (v === '' || v == null || v === defaults[k]) next.delete(k); else next.set(k, String(v)); });
    if (resetPage && !('page' in patch)) next.delete('page');
    setSp(next, { replace: false });
  };
  return { get, page, pageSize, setPage: (p) => update({ page: p <= 1 ? '' : p }, { resetPage: false }), setPageSize: (n) => update({ page_size: n === 25 ? '' : n }), update };
};

const range = (page, pages) => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const set = new Set([1, 2, pages - 1, pages, page - 1, page, page + 1, ...(page <= 2 ? [3, 4] : []), ...(page >= pages - 1 ? [pages - 2, pages - 3] : [])].filter(n => n >= 1 && n <= pages));
  const out = []; let prev = 0;
  [...set].sort((a, b) => a - b).forEach(n => { if (n - prev > 1) out.push('…'); out.push(n); prev = n; });
  return out;
};

export const Pagination = ({ total = 0, page = 1, pageSize = 25, onPage, onPageSize, noun = 'records', testid = 'pagination', sizes = PAGE_SIZES }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const btn = 'crm-control h-8 min-w-8 px-2 rounded-md text-sm inline-flex items-center justify-center border transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm" data-testid={testid}>
      <div className="text-slate-600" data-testid={`${testid}-summary`}>
        Showing <span className="font-semibold text-slate-900">{from}–{to}</span> of <span className="font-semibold text-slate-900">{total}</span> {noun}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-1.5 text-slate-500">Per page
          <select value={pageSize} onChange={e => onPageSize(Number(e.target.value))} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid={`${testid}-size`}>
            {sizes.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <nav className="flex items-center gap-1" aria-label="Pagination">
          <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1} className={`${btn} border-slate-200 text-slate-700 hover:bg-slate-50`} data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /> Previous</button>
          {range(page, pages).map((n, i) => n === '…'
            ? <span key={`e${i}`} className="px-1 text-slate-400">…</span>
            : <button key={n} type="button" onClick={() => onPage(n)} aria-current={n === page ? 'page' : undefined}
                className={`${btn} ${n === page ? 'bg-violet-600 border-violet-600 text-white shadow-sm' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-testid={`${testid}-page-${n}`}>{n}</button>)}
          <button type="button" onClick={() => onPage(page + 1)} disabled={page >= pages} className={`${btn} border-slate-200 text-slate-700 hover:bg-slate-50`} data-testid={`${testid}-next`}>Next <ChevronRight className="h-4 w-4" /></button>
        </nav>
      </div>
    </div>
  );
};
